<template>
  <rmr-modal :value="value" max-width="920px" variant="form" :title="video ? video.title : 'Fiche vidéo'" :eyebrow="eyebrow" :icon="icons.mdiYoutube" scrollable @input="$emit('input', $event)">
    <template #header-subtitle>
      <p v-if="video" class="yt-vm__meta">
        {{ video.site_name }}<span v-if="universes"> · {{ universes }}</span> · publiée le {{ fDateTime(video.published_at) }} · {{ fDuration(video.duration_s) }} ·
        <a :href="`https://www.youtube.com/watch?v=${video.video_id}`" target="_blank" rel="noopener"><code>{{ video.video_id }}</code></a>
      </p>
    </template>
    <template #body>
      <div v-if="loading" class="yt-vm__loading"><v-progress-circular indeterminate :color="indigo" size="28" width="3" /></div>
      <div v-else-if="video" class="yt-vm">
        <div class="yt-vm__top">
          <img v-if="video.thumbnail_url" :src="video.thumbnail_url" alt="" class="yt-vm__thumb" />
          <div class="yt-vm__kpis">
            <div v-for="item in kpis" :key="item.label" class="yt-vm__kpi">
              <span>{{ item.label }}</span>
              <b>{{ item.value }}</b>
            </div>
          </div>
        </div>
        <p class="yt-vm__period">Chiffres sur la période : {{ periodLabel }}</p>
        <div class="yt-vm__charts">
          <div>
            <h4>Vues par jour</h4>
            <vue-apex-charts type="area" height="180" :options="chartOptions(colors.views, fInt)" :series="[{ name: 'Vues', data: video.daily.map((d) => d.views) }]" />
          </div>
          <div>
            <h4>Impressions Discover par jour</h4>
            <vue-apex-charts type="bar" height="180" :options="chartOptions(colors.discover, fInt)" :series="[{ name: 'Impressions', data: video.daily.map((d) => d.discover_impressions) }]" />
          </div>
        </div>
      </div>
    </template>
  </rmr-modal>
</template>

<script>
import { mdiYoutube } from '@mdi/js'
import { computed, ref, toRefs, watch } from '@vue/composition-api'
import VueApexCharts from 'vue-apexcharts'
import RmrModal from '@/components/ui/layout/RmrModal.vue'
import { RMR_INDIGO } from '@/styles/tokens'
import useYoutubePerf from '../useYoutubePerf'
import { COLORS, baseChartOptions, fDateTime, fDay, fDayLong, fDuration, fEur, fEur2, fInt, fPct, fPct100 } from '../ytFormat'

export default {
  name: 'YtVideoModal',
  components: { RmrModal, VueApexCharts },
  props: {
    value: { type: Boolean, default: false },
    videoId: { type: String, default: null },
  },
  setup(props) {
    const { value, videoId } = toRefs(props)
    const { get, label } = useYoutubePerf()
    const video = ref(null)
    const loading = ref(false)

    const load = async () => {
      if (!value.value || !videoId.value) return
      loading.value = true
      video.value = null
      try {
        const { data } = await get(`/videos/${videoId.value}`)
        video.value = data
      } finally {
        loading.value = false
      }
    }
    watch([value, videoId], load, { immediate: true })

    const universes = computed(() => (video.value?.universes || []).map((u) => u.name).join(', '))
    const eyebrow = computed(() => (video.value ? video.value.site_name : 'Vidéo'))
    const kpis = computed(() => {
      const v = video.value
      if (!v) return []
      return [
        { label: 'Vues', value: fInt(v.views) },
        { label: 'Revenus', value: fEur(v.revenue) },
        { label: 'RPM', value: fEur2(v.rpm) },
        { label: 'Durée moyenne', value: fDuration(v.avg_duration) },
        { label: '% vu', value: fPct100(v.avg_percentage) },
        { label: 'Likes', value: fInt(v.likes) },
        { label: 'Dislikes', value: fInt(v.dislikes) },
        { label: 'Commentaires', value: fInt(v.comments) },
        { label: 'Partages', value: fInt(v.shares) },
        { label: 'Impressions Discover', value: fInt(v.discover_impressions) },
        { label: 'Vues venues de Discover', value: fInt(v.discover_views) },
        { label: 'CTR Discover', value: fPct(v.discover_ctr, 2) },
      ]
    })
    const chartOptions = (color, formatter) => ({
      ...baseChartOptions(),
      colors: [color],
      stroke: { width: 2, curve: 'smooth' },
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 100] } },
      plotOptions: { bar: { borderRadius: 3, columnWidth: '70%' } },
      xaxis: { ...baseChartOptions().xaxis, categories: (video.value?.daily || []).map((d) => fDay(d.date)), tickAmount: 8 },
      yaxis: { labels: { style: { colors: '#94A3B8', fontSize: '10px', fontWeight: 600 }, formatter: (v) => formatter(v) }, min: 0, forceNiceScale: true },
      tooltip: { x: { formatter: (_, { dataPointIndex }) => fDayLong(video.value?.daily?.[dataPointIndex]?.date || '') }, y: { formatter: (v) => formatter(v) } },
    })

    return { video, loading, universes, eyebrow, kpis, chartOptions, periodLabel: label, colors: COLORS, indigo: RMR_INDIGO, icons: { mdiYoutube }, fInt, fDuration, fDateTime }
  },
}
</script>

<style lang="scss" scoped>
.yt-vm { padding: $rmr-space-md $rmr-space-xl $rmr-space-xl; display: flex; flex-direction: column; gap: $rmr-space-md; }
.yt-vm__loading { display: flex; justify-content: center; padding: 48px; }
.yt-vm__meta { margin: 4px 0 0; font-size: $rmr-fs-sm; color: $rmr-text-muted; a { color: $rmr-indigo; } }
.yt-vm__top { display: grid; grid-template-columns: 200px minmax(0, 1fr); gap: $rmr-space-lg; align-items: start; @media (max-width: 700px) { grid-template-columns: 1fr; } }
.yt-vm__thumb { width: 100%; aspect-ratio: 16 / 9; object-fit: cover; border-radius: $rmr-radius-lg; background: $rmr-surface-2; }
.yt-vm__kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; @media (max-width: 900px) { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.yt-vm__kpi {
  display: flex; flex-direction: column; gap: 2px; padding: 8px 10px; border-radius: $rmr-radius-md; background: $rmr-surface-2;
  span { font-size: $rmr-fs-xs; color: $rmr-text-subtle; font-weight: 600; }
  b { font-size: $rmr-fs-md; color: $rmr-text-primary; font-variant-numeric: tabular-nums; }
}
.yt-vm__period { margin: 0; font-size: $rmr-fs-xs; color: $rmr-text-subtle; }
.yt-vm__charts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $rmr-space-md; @media (max-width: 700px) { grid-template-columns: 1fr; }
  h4 { margin: 0 0 4px; font-size: $rmr-fs-sm; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: $rmr-indigo-dark; }
}
</style>
